import type { ReactNode } from 'react'
import { motion } from 'framer-motion'
import type { HTMLMotionProps } from 'framer-motion'

interface CardProps extends Omit<HTMLMotionProps<'div'>, 'children'> {
  children: ReactNode
  variant?: 'default' | 'glass' | 'elevated' | 'premium' | 'floating' | 'gradient' | 'outline' | 'glow' | 'minimal'
  padding?: 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  hover?: boolean
  bordered?: boolean
  shadow?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | 'premium'
}

const Card = ({ 
  children, 
  variant = 'default',
  padding = 'md',
  hover = true,
  bordered = false,
  shadow,
  className = '', 
  ...props 
}: CardProps) => {
  const baseClasses = 'rounded-3xl transition-all duration-300 relative overflow-hidden'
  
  const variantClasses = {
    default: 'bg-white border border-gray-100 shadow-premium-sm hover:shadow-premium-md',
    glass: 'bg-white/20 backdrop-blur-2xl border border-white/20 shadow-glass-shadow hover:bg-white/30 hover:border-white/30',
    elevated: 'bg-white shadow-premium-lg hover:shadow-premium-xl transform hover:scale-[1.01] hover:-translate-y-1',
    premium: 'bg-gradient-to-br from-white via-gray-50/50 to-white border border-gray-100 shadow-premium-md hover:shadow-premium-lg hover:border-gray-200',
    floating: 'bg-white shadow-floating hover:shadow-card-hover transform hover:scale-[1.02] hover:-translate-y-2',
    gradient: 'bg-gradient-to-br from-primary-50/50 via-white to-secondary-50/50 border border-primary-100/50 shadow-premium-md hover:shadow-premium-lg hover:from-primary-50 hover:to-secondary-50',
    outline: 'bg-white border-2 border-primary-200 hover:border-primary-300 shadow-premium-sm hover:shadow-premium',
    glow: 'bg-white border border-primary-100 shadow-premium-glow hover:shadow-premium-glow-lg transform hover:scale-[1.01]',
    minimal: 'bg-white/80 backdrop-blur-sm hover:bg-white/90'
  }
  
  const paddingClasses = {
    none: '',
    xs: 'p-3',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
    xl: 'p-12'
  }

  const shadowClasses = shadow ? {
    none: '',
    sm: 'shadow-premium-sm',
    md: 'shadow-premium-md', 
    lg: 'shadow-premium-lg',
    xl: 'shadow-premium-xl',
    premium: 'shadow-premium-2xl'
  }[shadow] : ''

  const borderClass = bordered ? 'border border-gray-200' : ''
  
  const hoverClasses = hover ? 'hover:shadow-premium-lg transition-all duration-300' : ''

  const finalClasses = [
    baseClasses,
    variantClasses[variant],
    paddingClasses[padding],
    shadowClasses,
    borderClass,
    hoverClasses,
    className
  ].filter(Boolean).join(' ')

  return (
    <motion.div
      className={finalClasses}
      whileHover={hover ? {
        y: variant === 'floating' ? -8 : variant === 'elevated' ? -4 : -2,
        transition: { duration: 0.2, ease: "easeOut" }
      } : {}}
      transition={{ 
        duration: 0.4, 
        ease: [0.25, 0.46, 0.45, 0.94] 
      }}
      {...props}
    >
      {/* Premium shimmer effect for premium variants */}
      {(variant === 'premium' || variant === 'gradient' || variant === 'glow') && (
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent pointer-events-none"
          initial={{ x: '-100%', opacity: 0 }}
          animate={{ x: '100%', opacity: 1 }}
          transition={{
            duration: 2,
            repeat: Infinity,
            repeatDelay: 4,
            ease: "easeInOut"
          }}
        />
      )}

      {/* Gradient overlay for enhanced depth */}
      {variant === 'premium' && (
        <div className="absolute inset-0 bg-gradient-to-br from-white/50 via-transparent to-gray-50/30 pointer-events-none" />
      )}

      {/* Content */}
      <div className="relative z-10">
        {children}
      </div>

      {/* Glow effect border for glow variant */}
      {variant === 'glow' && (
        <motion.div
          className="absolute inset-0 rounded-3xl border border-primary-200/50"
          animate={{
            borderColor: ['rgba(59, 130, 246, 0.3)', 'rgba(147, 51, 234, 0.3)', 'rgba(59, 130, 246, 0.3)']
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      )}
    </motion.div>
  )
}

export default Card